import { useSecondaryPage } from '@/PageManager'
import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerOverlay } from '@/components/ui/drawer'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { toRelay } from '@/lib/link'
import { normalizeUrl, simplifyUrl } from '@/lib/url'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import client from '@/services/client.service'
import { usePendingPublishMap } from '@/lib/pendingPublish'
import { discardPendingEvent, pendingIdsAtom, savePendingEvent } from '@/atoms/pending'
import { HugeiconsIcon } from '@hugeicons/react'
import { CloudUploadIcon, Delete01Icon, AlertCircleIcon, Server } from '@hugeicons/core-free-icons'
import { Event } from '@nostr/tools/wasm'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import RelayIcon from '../RelayIcon'
import { isLocal } from '@nostr/gadgets/redstore'
import { cn } from '@/lib/utils'
import LocalDatabaseMenuButton from './LocalDatabaseMenuButton'
import { useAtomValue } from 'jotai'
import { currentPubkeyAtom } from '@/atoms/session'
import { fetchFavoriteRelaysWithSets, loadRelayList } from '@nostr/gadgets/lists'
import { Avatar } from '@/components/ui/avatar'

export default function SeenOnButton({ event }: { event: Event }) {
  const { t } = useTranslation()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const { push } = useSecondaryPage()
  const pendingIds = useAtomValue(pendingIdsAtom)
  const pendingPublishMap = usePendingPublishMap()
  const currentPubkey = useAtomValue(currentPubkeyAtom)
  const [relays, setRelays] = useState<string[]>([])
  const [presenceRelays, setPresenceRelays] = useState<string[]>([])
  const [isPresenceMenuOpen, setIsPresenceMenuOpen] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isEventLocal, setIsEventLocal] = useState(() => isLocal(event))
  const [isRepublishing, setIsRepublishing] = useState(false)

  const isPending = useMemo(
    () => pendingIds.includes(event.id) || pendingPublishMap.has(event.id),
    [pendingIds, pendingPublishMap, event.id]
  )

  useEffect(() => {
    const seenOn = client.getSeenEventRelayUrls(event.id, event)
    setRelays(seenOn)
  }, [event.id, event])

  useEffect(() => {
    let cancelled = false
    const loadPresenceRelays = async () => {
      const candidates: string[] = []
      const add = (url: string) => {
        url = normalizeUrl(url)
        if (relays.includes(url)) return
        if (candidates.includes(url)) return
        candidates.push(url)
      }

      if (currentPubkey) {
        const [our, favorites] = await Promise.all([
          loadRelayList(currentPubkey),
          fetchFavoriteRelaysWithSets(currentPubkey)
        ])
        our.items.forEach((relay) => add(relay.url))
        for (const favorite of favorites) {
          if (typeof favorite === 'string') add(favorite)
          else favorite.items.forEach(add)
        }
      }

      const taggedPubkeys = event.tags
        .filter(([tag]) => tag === 'p' || tag === 'P')
        .map(([, pubkey]) => pubkey)
        .filter((pubkey): pubkey is string => !!pubkey)
      const pubkeys = Array.from(new Set([event.pubkey, ...taggedPubkeys]))
      const relayLists = await Promise.all(pubkeys.map((pubkey) => loadRelayList(pubkey)))
      relayLists.forEach((list, index) => {
        list.items
          .filter((relay) => (index === 0 ? relay.write : relay.read))
          .forEach((relay) => add(relay.url))
      })
      window.hallway.universe.bigRelayUrls.forEach(add)
      if (!cancelled) setPresenceRelays(candidates)
    }
    void loadPresenceRelays()
    return () => {
      cancelled = true
    }
  }, [currentPubkey, event, relays, t])

  const checkPresence = async (relay: string) => {
    try {
      const events = await client.fetchEvents([relay], { ids: [event.id] })
      if (events.length === 0) throw new Error(t('Event not found'))
      await client.markEventSeenOn(event, relay)
      setRelays((current) =>
        current.some((url) => normalizeUrl(url) === normalizeUrl(relay))
          ? current
          : [...current, relay]
      )
      toast.success(t('Found on {{relay}}', { relay: simplifyUrl(relay) }))
    } catch {
      toast.error(t('Not found on {{relay}}', { relay: simplifyUrl(relay) }))
    }
  }

  const presenceItems = presenceRelays.map((url) => ({
    url,
    label: simplifyUrl(url)
  }))

  const trigger = (
    <button
      className={cn(
        'flex gap-1 items-center text-muted-foreground enabled:hover:text-primary pl-3 h-full',
        isPending && 'text-destructive hover:text-destructive'
      )}
      title={isPending ? t('Not published') : t('Seen on')}
      disabled={relays.length === 0 && !isPending}
      onClick={() => {
        if (isSmallScreen) {
          setIsDrawerOpen(true)
        }
      }}
    >
      {isPending ? (
        <HugeiconsIcon icon={AlertCircleIcon} className="animate-pulse" />
      ) : (
        <HugeiconsIcon icon={Server} />
      )}
      {relays.length > 0 && <div className="text-sm">{relays.length}</div>}
    </button>
  )

  if (isSmallScreen) {
    return (
      <>
        {trigger}
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerOverlay onClick={() => setIsDrawerOpen(false)} />
          <DrawerContent hideOverlay>
            <div className="py-2">
              <Button
                className="w-full p-6 justify-start text-lg gap-4"
                variant="ghost"
                onClick={() => setIsPresenceMenuOpen(true)}
              >
                <Avatar className="w-6 h-6 flex items-center justify-center">
                  <HugeiconsIcon icon={Server} size={14} />
                </Avatar>{' '}
                {t('Check presence')}
              </Button>
              <Drawer open={isPresenceMenuOpen} onOpenChange={setIsPresenceMenuOpen}>
                <DrawerOverlay onClick={() => setIsPresenceMenuOpen(false)} />
                <DrawerContent hideOverlay>
                  <div className="py-2">
                    <div className="px-6 py-4 text-lg font-medium">{t('Check presence')}</div>
                    {presenceItems.map(({ url, label }) => (
                      <Button
                        className="w-full p-6 justify-start text-lg gap-4"
                        variant="ghost"
                        key={url}
                        onClick={() => {
                          setIsPresenceMenuOpen(false)
                          void checkPresence(url)
                        }}
                      >
                        <RelayIcon url={url} /> {label}
                      </Button>
                    ))}
                    <Button
                      className="w-full p-6 justify-start text-lg gap-4"
                      variant="ghost"
                      onClick={() => {
                        setIsPresenceMenuOpen(false)
                        checkCustomPresence()
                      }}
                    >
                      <Avatar className="w-6 h-6 flex items-center justify-center">
                        <HugeiconsIcon icon={Server} size={14} />
                      </Avatar>{' '}
                      {t('Custom relay URL')}
                    </Button>
                  </div>
                </DrawerContent>
              </Drawer>
              {isPending && (
                <>
                  <div className="px-6 py-4 flex items-center gap-4 text-lg font-medium text-destructive">
                    <HugeiconsIcon icon={AlertCircleIcon} />{' '}
                    {isRepublishing ? t('Publishing...') : t('Not published')}
                  </div>
                  <Button
                    className="w-full p-6 justify-start text-lg gap-4"
                    variant="ghost"
                    onClick={republish}
                    disabled={isRepublishing}
                  >
                    <HugeiconsIcon icon={CloudUploadIcon} /> {t('Publish')}
                  </Button>
                  <Button
                    className="w-full p-6 justify-start text-lg gap-4 text-destructive"
                    variant="ghost"
                    onClick={discard}
                  >
                    <HugeiconsIcon icon={Delete01Icon} /> {t('Discard')}
                  </Button>
                </>
              )}
              {!isPending && isEventLocal && (
                <LocalDatabaseMenuButton
                  event={event}
                  is={isEventLocal}
                  mode="drawer"
                  onAction={() => {
                    setIsEventLocal(false)
                  }}
                />
              )}
              {relays.map((relay) => (
                <Button
                  className="w-full p-6 justify-start text-lg gap-4"
                  variant="ghost"
                  key={relay}
                  onClick={() => {
                    setIsDrawerOpen(false)
                    setTimeout(() => {
                      push(toRelay(relay))
                    }, 50)
                  }}
                >
                  <RelayIcon url={relay} /> {simplifyUrl(relay)}
                </Button>
              ))}
              {!isEventLocal && (
                <>
                  <div className="px-6 pt-2 pb-1 text-sm font-medium text-muted-foreground">
                    {t('Not on')}
                  </div>
                  <LocalDatabaseMenuButton
                    event={event}
                    is={isEventLocal}
                    mode="drawer"
                    onAction={() => {
                      setIsEventLocal(true)
                    }}
                  />
                </>
              )}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent>
        {isPending && (
          <>
            <DropdownMenuLabel className="text-destructive flex items-center gap-2">
              <HugeiconsIcon icon={AlertCircleIcon} className="size-4" />
              {isRepublishing ? t('Publishing...') : t('Not published')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={republish} disabled={isRepublishing}>
              <HugeiconsIcon icon={CloudUploadIcon} className="size-4" />
              {t('Publish')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={discard} className="text-destructive focus:text-destructive">
              <HugeiconsIcon icon={Delete01Icon} className="size-4" />
              {t('Discard')}
            </DropdownMenuItem>
          </>
        )}
        {!isPending && (
          <>
            {(relays.length || isEventLocal) && (
              <>
                <DropdownMenuLabel>{t('Seen on')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {relays.map((relay) => (
                  <DropdownMenuItem
                    key={relay}
                    onClick={() => push(toRelay(relay))}
                    className="min-w-52"
                  >
                    <RelayIcon url={relay} />
                    {simplifyUrl(relay)}
                  </DropdownMenuItem>
                ))}
                {isEventLocal && (
                  <LocalDatabaseMenuButton
                    event={event}
                    is={isEventLocal}
                    mode="dropdown"
                    onAction={() => setIsEventLocal(false)}
                  />
                )}
              </>
            )}
            {!isEventLocal && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t('Not on')}</DropdownMenuLabel>
                <LocalDatabaseMenuButton
                  event={event}
                  is={isEventLocal}
                  mode="dropdown"
                  onAction={() => setIsEventLocal(true)}
                />
              </>
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="min-w-52">
            <Avatar className="w-6 h-6 flex items-center justify-center">
              <HugeiconsIcon icon={Server} size={14} />
            </Avatar>
            {t('Check presence')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-[50vh] overflow-y-auto">
            {presenceItems.map(({ url, label }) => (
              <DropdownMenuItem key={url} onClick={() => void checkPresence(url)}>
                <RelayIcon url={url} /> <div className="max-w-64 truncate">{label}</div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={checkCustomPresence}>
              <Avatar className="w-6 h-6 flex items-center justify-center">
                <HugeiconsIcon icon={Server} size={14} />
              </Avatar>
              {t('Custom relay URL')}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  function checkCustomPresence() {
    const input = prompt(t('Enter relay URL (e.g. wss://relay.example.com)'))
    if (!input) return
    let url = input.trim()
    if (!/^wss?:\/\//i.test(url)) url = 'wss://' + url
    void checkPresence(url)
  }

  async function republish() {
    if (isRepublishing) return

    setIsRepublishing(true)
    try {
      const relayUrls = await client.determineTargetRelays(event)
      await client.publishEvent(relayUrls, event)
      discardPendingEvent(event.id)
      toast.success(t('Published successfully!'), { duration: 2000 })
      const seenOn = client.getSeenEventRelayUrls(event.id, event)
      setRelays(seenOn)
    } catch (error) {
      savePendingEvent(event)
      console.warn('saved as pending.')
      toast.error(t('Saved as pending.'))
      const errors = error instanceof AggregateError ? error.errors : [error]
      for (const err of errors) {
        console.warn('failed to post:', err instanceof Error ? err.message : String(err))
        toast.error(
          t('Failed to post: {{error}}', {
            error: err instanceof Error ? err.message : String(err)
          }),
          {
            duration: 10_000
          }
        )
      }
    } finally {
      setIsRepublishing(false)
      setIsDrawerOpen(false)
    }
  }

  function discard() {
    client.removeEventFromCache(event.id)
    discardPendingEvent(event.id)
    setIsDrawerOpen(false)
    toast.success(t('Discarded.'))
  }
}
